import { ArrowDown, ArrowUp, MessageSquare, X } from "lucide-react"
import { useEffect, useRef } from "react"

import {
  MessageScroller,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
  useMessageScroller,
  useMessageScrollerScrollable,
} from "@/components/ui/message-scroller"
import {
  BookingCalendarCard,
  type BookingCompletion,
  type BookingPrefill,
} from "@/components/widget/booking-calendar-card"
import { cn } from "@/lib/utils"

// IMP-429: the presentational website-chat widget — the launcher, chat window,
// message stream, composer, conversation starters, typing indicator, and
// proactive greeting teaser. It's a CONTROLLED component: all state (open,
// messages, input, sending, teaser) and behavior (onSend, onBooked) come from
// the parent, so the live embedded widget (pages/public/website-chat-widget) and
// the setup Preview render the exact same surface — appearance stays in sync by
// construction rather than being maintained in two places.

export interface TextMessage {
  role: "bot" | "visitor"
  text: string
}

// IMP-395: the embedded booking calendar, a message-level card in the stream.
export interface CalendarMessage {
  role: "calendar"
  provider: "calendly" | "cal_com"
  url: string
  prefill: BookingPrefill
  status: "open" | "booked"
}

export type WidgetMessage = TextMessage | CalendarMessage

// The appearance fields the widget renders from. Both the live agent config and
// the setup form's ChatbotSetupData satisfy this shape.
export interface WidgetAppearance {
  bot_name: string
  primary_color: string
  bubble_position: "bottom-right" | "bottom-left"
  welcome_message: string
  conversation_starters: string[]
}

interface WebsiteChatWidgetViewProps {
  config: WidgetAppearance
  messages: WidgetMessage[]
  input: string
  sending: boolean
  open: boolean
  teaser: boolean
  // IMP-454: when true, the welcome message renders as the thread's first bot
  // message. Display only — the parent decides (fresh conversation), and it's
  // never stored with the transcript or sent to the model.
  greeting?: boolean
  onOpenChange: (open: boolean) => void
  onInputChange: (value: string) => void
  onSend: (text?: string) => void
  onDismissTeaser?: () => void
  onBooked?: (index: number, completion: BookingCompletion) => void
  // The live widget fills the iframe viewport (fixed); the preview sits inside a
  // positioned panel (absolute).
  positioning?: "fixed" | "absolute"
}

// Partner-supplied primary color drives the accent roles (send button, launcher).
// These derive the tints/rings we need from that single hex without a color lib.
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim())
  if (!m) return null
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  }
}

function withAlpha(hex: string, alpha: number): string {
  const c = hexToRgb(hex)
  return c ? `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})` : hex
}

export function WebsiteChatWidgetView({
  config,
  messages,
  input,
  sending,
  open,
  teaser,
  greeting = false,
  onOpenChange,
  onInputChange,
  onSend,
  onDismissTeaser,
  onBooked,
  positioning = "fixed",
}: WebsiteChatWidgetViewProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const isLeft = config.bubble_position === "bottom-left"
  const color = config.primary_color
  const starters = config.conversation_starters.filter((s) => s.trim())
  const showStarters = messages.length === 0 && starters.length > 0 && !sending
  const showGreeting = greeting && config.welcome_message.trim().length > 0

  // On open, focus the composer. The transcript's scroll position and its
  // follow-the-live-edge behavior are owned by MessageScroller below.
  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [open])

  return (
    <>
      <style>{`
        @keyframes imp-wc-pop { from { opacity: 0; transform: translateY(12px) scale(.97) } to { opacity: 1; transform: none } }
        @keyframes imp-wc-rise { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
        @media (prefers-reduced-motion: reduce) { .imp-wc-anim { animation: none !important } }
      `}</style>

      <div
        className={cn(
          "flex flex-col gap-3",
          positioning === "fixed"
            ? "fixed bottom-3"
            : // In the setup preview the widget lives inside a small frame, so
              // span that frame and let the panel size to it — the viewport
              // units below would overflow the frame on any short container.
              "absolute inset-3 justify-end",
          isLeft ? "left-3 items-start" : "right-3 items-end",
        )}
      >
        {/* Chat window */}
        {open && (
          <div
            className={cn(
              "imp-wc-anim flex max-h-[640px] flex-col overflow-hidden rounded-2xl bg-white text-zinc-900 shadow-[0_2px_14px_rgba(0,0,0,0.12),0_12px_28px_-8px_rgba(0,0,0,0.2)]",
              positioning === "fixed"
                ? "h-[calc(100dvh-5.5rem)] w-[min(92vw,384px)]"
                : "w-full min-w-0 flex-1 basis-0 md:max-w-[384px]",
            )}
            style={{ animation: "imp-wc-pop .18s ease-out" }}
          >
            {/* Header — minimal white bar; a static "AI Assistant" descriptor
                under the bot name (IMP-422: the greeting stays in the teaser
                and the transcript, not the heading). The partner's assistant
                name is deliberately not used: this renders on the client's own
                site, not a partner surface. */}
            <header
              className="flex shrink-0 items-start justify-between gap-3 px-5 py-4"
              style={{ backgroundColor: color }}
            >
              <div className="min-w-0">
                <div className="truncate text-base font-semibold text-white">
                  {config.bot_name}
                </div>
                <div className="mt-0.5 line-clamp-1 text-sm text-white/70">
                  AI Assistant
                </div>
              </div>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-white/25 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Close chat"
              >
                <X className="size-4" />
              </button>
            </header>

            {/* Stream — bottom-pinned: MessageScroller follows the live edge as
                replies land, and we scroll to the bottom on every send (see
                FollowOnSend). The jump-to-latest pill covers scrolled-up reads. */}
            <MessageScrollerProvider autoScroll defaultScrollPosition="end">
              <MessageScroller className="min-h-0 flex-1">
                <MessageScrollerViewport
                  aria-label="Messages"
                  className="bg-white px-5 py-5"
                >
                  <MessageScrollerContent className="gap-5">
                    {/* IMP-454: the welcome message opens the thread as a bot
                        message on a fresh conversation. */}
                    {showGreeting && (
                      <MessageScrollerItem messageId="greeting">
                        <div className="text-[15px] leading-relaxed whitespace-pre-wrap text-zinc-800">
                          {config.welcome_message}
                        </div>
                      </MessageScrollerItem>
                    )}

                    {messages.map((msg, i) => {
                      if (msg.role === "calendar") {
                        return (
                          <MessageScrollerItem key={i} messageId={`m-${i}`}>
                            <BookingCalendarCard
                              provider={msg.provider}
                              url={msg.url}
                              prefill={msg.prefill}
                              status={msg.status}
                              color={color}
                              onBooked={(completion) =>
                                onBooked?.(i, completion)
                              }
                            />
                          </MessageScrollerItem>
                        )
                      }

                      // Assistant replies read as plain text — no bubble, no
                      // avatar — so the answer feels like the widget speaking.
                      if (msg.role === "bot") {
                        return (
                          <MessageScrollerItem key={i} messageId={`m-${i}`}>
                            <div className="text-[15px] leading-relaxed whitespace-pre-wrap text-zinc-800">
                              {msg.text}
                            </div>
                          </MessageScrollerItem>
                        )
                      }

                      return (
                        <MessageScrollerItem
                          key={i}
                          messageId={`m-${i}`}
                          className="flex justify-end"
                        >
                          <div className="max-w-[85%] rounded-2xl bg-zinc-100 px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap text-zinc-900">
                            {msg.text}
                          </div>
                        </MessageScrollerItem>
                      )
                    })}

                    {sending && (
                      <MessageScrollerItem messageId="typing">
                        {/* py-1 keeps the bounce inside the item box — the
                            scroller's content-visibility paint containment
                            clips anything that animates outside it. */}
                        <div className="flex items-center gap-1 py-1">
                          <Dot delay="0ms" />
                          <Dot delay="150ms" />
                          <Dot delay="300ms" />
                        </div>
                      </MessageScrollerItem>
                    )}

                    {showStarters && (
                      <MessageScrollerItem className="flex flex-col items-start gap-2">
                        {starters.map((starter, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => onSend(starter)}
                            className="cursor-pointer rounded-full border border-zinc-200 bg-white px-3.5 py-2 text-left text-sm text-zinc-700 transition-colors hover:bg-zinc-50"
                          >
                            {starter}
                          </button>
                        ))}
                      </MessageScrollerItem>
                    )}
                  </MessageScrollerContent>
                </MessageScrollerViewport>

                <JumpToLatest />
                <FollowOnSend sending={sending} />
              </MessageScroller>
            </MessageScrollerProvider>

            {/* Composer — pill with a brand-accent send button. */}
            <form
              className="shrink-0 p-3"
              onSubmit={(e) => {
                e.preventDefault()
                onSend()
              }}
            >
              <div className="flex items-center gap-2 rounded-[22px] bg-zinc-100 py-1.5 pr-1.5 pl-4">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => onInputChange(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 border-0 bg-transparent py-2 text-[15px] text-zinc-900 shadow-none outline-none placeholder:text-zinc-400 focus:border-0 focus:ring-0"
                  aria-label="Message"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || sending}
                  className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-white transition-all hover:brightness-110 disabled:cursor-default disabled:opacity-30"
                  style={{ backgroundColor: color }}
                  aria-label="Send message"
                >
                  <ArrowUp className="size-4" />
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Greeting teaser — a one-time proactive nudge before first open. */}
        {!open && teaser && (
          <div
            className="imp-wc-anim flex items-start gap-1"
            style={{ animation: "imp-wc-rise .2s ease-out" }}
          >
            <button
              type="button"
              onClick={() => onOpenChange(true)}
              className={cn(
                "flex max-w-[248px] cursor-pointer items-start gap-2.5 rounded-2xl border border-black/5 bg-white px-3.5 py-3 text-left text-sm leading-snug text-zinc-800 shadow-xl",
                isLeft ? "rounded-bl-md" : "rounded-br-md",
              )}
            >
              <span
                className="flex size-6 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: withAlpha(color, 0.12), color }}
              >
                <MessageSquare className="size-3.5" />
              </span>
              <span className="flex-1">{config.welcome_message}</span>
            </button>
            <button
              type="button"
              onClick={onDismissTeaser}
              className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white text-zinc-400 shadow transition-colors hover:text-zinc-700"
              aria-label="Dismiss"
            >
              <X className="size-3" />
            </button>
          </div>
        )}

        {/* Launcher — softened brand-accent glow. */}
        <button
          type="button"
          onClick={() => onOpenChange(!open)}
          className="flex size-12 cursor-pointer items-center justify-center rounded-full text-white hover:bg-linear-160 hover:from-white/20 hover:to-white/0 hover:to-45%"
          style={{
            backgroundColor: color,
            boxShadow: `inset 0 0 4px rgba(255,255,255,0.5), inset 0 0 0 1px rgba(0,0,0,0.1), 0 6px 18px ${withAlpha(color, 0.32)}`,
          }}
          aria-label={open ? "Close chat" : "Open chat"}
        >
          {open ? (
            <X className="size-5" />
          ) : (
            <MessageSquare className="size-5" />
          )}
        </button>
      </div>
    </>
  )
}

// Sending is an explicit action, so it always drops the visitor at the bottom —
// and scrollToEnd re-engages MessageScroller's follow-output, so the reply (and
// the typing indicator before it) keeps pushing the view down as it arrives.
function FollowOnSend({ sending }: { sending: boolean }) {
  const { scrollToEnd } = useMessageScroller()
  useEffect(() => {
    if (sending) scrollToEnd()
  }, [sending, scrollToEnd])
  return null
}

// The scroll-to-latest affordance, shown only while the visitor is scrolled up
// away from the live edge (MessageScroller reports this via `end`). Clicking it
// re-engages follow-output.
function JumpToLatest() {
  const { end } = useMessageScrollerScrollable()
  const { scrollToEnd } = useMessageScroller()
  if (!end) return null
  return (
    <button
      type="button"
      onClick={() => scrollToEnd()}
      className="imp-wc-anim absolute bottom-3 left-1/2 flex size-9 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 shadow-md transition-colors hover:text-zinc-900"
      style={{ animation: "imp-wc-rise .18s ease-out" }}
      aria-label="Scroll to latest message"
    >
      <ArrowDown className="size-4" />
    </button>
  )
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="imp-wc-anim size-1.5 animate-bounce rounded-full bg-zinc-400 motion-reduce:animate-none"
      style={{ animationDelay: delay }}
    />
  )
}
